import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import Link from "next/link";
import {
  Button,
  Card,
  DropdownSearch,
  type DropdownOption,
  Chart,
  HorizontalBarsChart,
  Checkbox,
  formatCurrency,
  formatNumber,
  cn,
  Tooltip,
  type DateRange,
  getColorFromCssVariable,
  EmptyStateIllustration4,
  CircleChart,
  DiffPercentageBadge,
  useDateInput,
} from "ui";

import { axiosApiInstance } from "../pages/api/axios";
import DurationPicker from "../components/DurationPicker";

import Layout from "../components/Layout";
import { twoFixedDigit } from "../utils/twoFixedDigit";
import {
  Heading,
  Subheading,
  CountMetrics,
  CountMetricsSkeleton,
  HorizontalBarsChartSkeleton,
  ChartSkeleton,
  SuccessAndAcceptanceRates,
  type DashboardMetrics,
  CircleChartSkeleton,
  RateItem,
  Tabs,
} from "../features/insights";
import { useStateWithDefaultUpdater } from "../hooks/useStateWithDefaultUpdater";
import { useMoneyHashQuery } from "../hooks/useMoneyHashQuery";
import { parseErrorsToArray } from "../utils/parseErrors";
import { useAnalytics } from "../features/analytics/analyticsProvider";
import { format, subDays } from "date-fns";
import { useFeatureFlags } from "../hooks/query";
import { InformationCircleIcon } from "icons/outline";
import { ErrorPage } from "../components/ErrorPage";
import { getAccountsWithSearch } from "../utils/dropdownsGetOptions";
import { useRouter } from "next/router";
import { isArray } from "../features/form/utils/isArray";
import { PageHeader } from "../components/PageHeading";
import { DatadogAnalytics } from "../features/insights/components/DatadogAnalytics";
import { useScrollSpy } from "../hooks/useScrollSpy";

const SECTION_TITLES: Record<string, string> = {
  overview: "Overview of your payments",
  revenue: "Revenue Overview",
  declines: "Decline overview",
  recovery: "Recovery overview",
  brands: "Brands overview",
};

export default function Dashboard() {
  const { featureFlags } = useFeatureFlags();
  const [dateRange, setDateRange] = useState<DateRange>();
  const router = useRouter();
  const { query } = router;
  const { analytics } = useAnalytics();

  const [
    selectedAccounts,
    setSelectedAccounts,
    {
      saveAsDefault: saveAccountsAsDefault,
      setSaveAsDefault: setSaveAccountsAsDefault,
    },
  ] = useStateWithDefaultUpdater<DropdownOption<string>[]>(
    "default-accounts",
    []
  );

  // Refs for section scrolling
  const overviewSectionRef = useRef<HTMLElement>(null);
  const revenueSectionRef = useRef<HTMLDivElement>(null);
  const declinesSectionRef = useRef<HTMLDivElement>(null);
  const recoverySectionRef = useRef<HTMLDivElement>(null);
  const brandsSectionRef = useRef<HTMLDivElement>(null);

  const [activeTab, setActiveTab] = useState<string>("overview");
  const [currentSectionTitle, setCurrentSectionTitle] = useState<string>(
    SECTION_TITLES.overview
  );
  const [isScrollSpyEnabled, setIsScrollSpyEnabled] = useState(true);

  const handleTabClick = useCallback((tab: string) => {
    setActiveTab(tab);
    setCurrentSectionTitle(SECTION_TITLES[tab]);
    setIsScrollSpyEnabled(false);

    const sectionMap: Record<
      string,
      React.RefObject<HTMLElement | HTMLDivElement>
    > = {
      overview: overviewSectionRef,
      revenue: revenueSectionRef,
      declines: declinesSectionRef,
      recovery: recoverySectionRef,
      brands: brandsSectionRef,
    };

    const targetRef = sectionMap[tab];
    if (targetRef?.current) {
      targetRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }

    setTimeout(() => {
      setIsScrollSpyEnabled(true);
    }, 800);
  }, []);

  // Memoize section refs array for scroll spy
  const sectionRefsArray = useMemo(
    () => [
      { id: "overview", ref: overviewSectionRef },
      { id: "revenue", ref: revenueSectionRef },
      { id: "declines", ref: declinesSectionRef },
      { id: "recovery", ref: recoverySectionRef },
      { id: "brands", ref: brandsSectionRef },
    ],
    []
  );

  useScrollSpy({
    sectionRefs: sectionRefsArray,
    onActiveChange: (activeId) => {
      setActiveTab(activeId);
      setCurrentSectionTitle(SECTION_TITLES[activeId]);
    },
    rootMargin: "-180px 0px -50% 0px",
    enabled: featureFlags.datadog_dashboard_analytics && isScrollSpyEnabled,
  });

  useEffect(() => {
    if (!featureFlags.new_dashboard_sr_and_ar) {
      const {
        start_date: _start_date,
        end_date: _end_date,
        organization: _organization,
        account: _account,
        ...rest
      } = query;

      router.replace({
        query: {
          ...rest,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureFlags.new_dashboard_sr_and_ar]);

  useEffect(() => {
    const startDate = router.query.start_date;
    const endDate = router.query.end_date;
    if (!startDate || !endDate) return;

    const now = new Date();
    const today = format(now, useDateInput.valueFormatString);
    const lastWeek = format(subDays(now, 7), useDateInput.valueFormatString);
    const last30Days = format(subDays(now, 30), useDateInput.valueFormatString);

    const isTodaySelected = startDate === today && endDate === today;
    const isLastWeekSelected = startDate === lastWeek && endDate === today;
    const isLast30DaysSelected = startDate === last30Days && endDate === today;

    const preset = isTodaySelected
      ? "today"
      : isLastWeekSelected
        ? "last_week"
        : isLast30DaysSelected
          ? "last_30_days"
          : "custom";

    analytics.track("home_dashboard_viewed", {
      preset,
    });
  }, [router.query.start_date, router.query.end_date, analytics]);

  useEffect(() => {
    window.Appcues?.track("analytics_dashboard.viewed");
  }, []);

  return (
    <Layout metaTitle="Home">
      <PageHeader
        title="Home"
        description="Get access to an overview of your payment hub in one place."
        withoutBorder={featureFlags.datadog_dashboard_analytics}
      />

      {featureFlags.new_dashboard_sr_and_ar && (
        <SuccessAndAcceptanceRates
          ref={overviewSectionRef}
          activeTab={activeTab}
          onTabChange={handleTabClick}
          sectionTitle={currentSectionTitle}
        />
      )}

      {featureFlags.datadog_dashboard_analytics ? (
        <>
          <DatadogAnalytics
            revenueSectionRef={revenueSectionRef}
            declinesSectionRef={declinesSectionRef}
            recoverySectionRef={recoverySectionRef}
            brandsSectionRef={brandsSectionRef}
          />
        </>
      ) : (
        <OldMetrics
          selectedAccounts={selectedAccounts}
          setSelectedAccounts={setSelectedAccounts}
          saveAsDefault={saveAccountsAsDefault}
          setSaveAsDefault={setSaveAccountsAsDefault}
          dateRange={dateRange}
          setDateRange={setDateRange}
        />
      )}
    </Layout>
  );
}

const OldMetrics = ({
  selectedAccounts,
  setSelectedAccounts,
  saveAsDefault,
  setSaveAsDefault,
  dateRange,
  setDateRange,
}: {
  selectedAccounts: DropdownOption<string>[];
  setSelectedAccounts: React.Dispatch<
    React.SetStateAction<DropdownOption<string>[]>
  >;
  saveAsDefault: boolean;
  setSaveAsDefault: React.Dispatch<React.SetStateAction<boolean>>;
  dateRange: DateRange | undefined;
  setDateRange: React.Dispatch<React.SetStateAction<DateRange | undefined>>;
}) => {
  const router = useRouter();
  const { account } = router.query;

  return (
    <>
      {selectedAccounts.length > 0 || !!account ? (
        <Insights
          selectedAccounts={selectedAccounts}
          setSelectedAccounts={setSelectedAccounts}
          saveAsDefault={saveAsDefault}
          setSaveAsDefault={setSaveAsDefault}
          dateRange={dateRange}
          setDateRange={setDateRange}
        />
      ) : (
        <Onboarding
          selectedAccounts={selectedAccounts}
          setSelectedAccounts={setSelectedAccounts}
          saveAsDefault={saveAsDefault}
          setSaveAsDefault={setSaveAsDefault}
        />
      )}
    </>
  );
};

const Onboarding = ({
  selectedAccounts,
  setSelectedAccounts,
  saveAsDefault,
  setSaveAsDefault,
}: {
  selectedAccounts: DropdownOption<string>[];
  setSelectedAccounts: React.Dispatch<
    React.SetStateAction<DropdownOption<string>[]>
  >;
  saveAsDefault: boolean;
  setSaveAsDefault: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { featureFlags } = useFeatureFlags();

  return (
    <div className="mt-20 flex flex-col items-center text-center">
      <EmptyStateIllustration4 />
      <h2 className="mt-1 font-medium text-foundations-boldest">
        Select an account to start
      </h2>
      <p className="text-foundations-subtle">
        Filter by account and date to fetch data on this page
      </p>
      {!featureFlags.new_dashboard_sr_and_ar && (
        <>
          <DropdownSearch
            multiple
            withConfirmation
            className="mt-8 max-w-xs"
            label="Select account"
            selected={selectedAccounts}
            onSelect={setSelectedAccounts}
            getOptions={(searchTerm) =>
              getAccountsWithSearch(searchTerm).then(
                (res) =>
                  res.data.data.map(({ columns }) => [
                    columns.id,
                    columns.name,
                  ]) || []
              )
            }
          />
          <label className="mt-4 flex items-center space-x-2 text-sm font-medium text-foundations-bolder">
            <Checkbox
              checked={saveAsDefault}
              onChange={(e) => setSaveAsDefault(e.target.checked)}
            />
            <span>Set as default account to preview data here </span>
          </label>
        </>
      )}
    </div>
  );
};

const Insights = ({
  selectedAccounts,
  setSelectedAccounts,
  saveAsDefault,
  setSaveAsDefault,
  dateRange,
  setDateRange,
}: {
  selectedAccounts: DropdownOption<string>[];
  setSelectedAccounts: React.Dispatch<
    React.SetStateAction<DropdownOption<string>[]>
  >;
  saveAsDefault: boolean;
  setSaveAsDefault: React.Dispatch<React.SetStateAction<boolean>>;
  dateRange: DateRange | undefined;
  setDateRange: React.Dispatch<React.SetStateAction<DateRange | undefined>>;
}) => {
  const router = useRouter();
  const { start_date, end_date, account } = router.query;
  const { from: startDate, to: endDate } = dateRange || {};
  const { analytics } = useAnalytics();
  const { featureFlags } = useFeatureFlags();

  const {
    data: dashboardMetrics,
    isLoading,
    isError,
    error,
  } = useMoneyHashQuery<DashboardMetrics>({
    queryKey: [
      "insights",
      {
        selectedAccounts: selectedAccounts,
        accountQuery: account,
        from: startDate,
        to: endDate,
        start_date,
        end_date,
      },
    ],
    queryFn: ({ signal }) =>
      getDashboardData({
        startDate: start_date ? new Date(start_date as string) : startDate!,
        endDate: end_date ? new Date(end_date as string) : endDate!,
        accountIds: account
          ? isArray(account)
            ? (account as string[]).join(",")
            : account
          : selectedAccounts.map((account) => account[0]).join(","),
        signal: signal,
      }),
    enabled: Boolean(startDate && endDate) || Boolean(start_date && end_date),
  });

  const showSkeleton = isLoading || isError || !dashboardMetrics;
  const skeletonState = isLoading ? "loading" : "error";
  const [isSearchingForAllAccounts, setIsSearchingForAllAccounts] =
    useState(false);

  useEffect(() => {
    async function getIsAllAccountsValue() {
      const totalAccounts = await getAccountsCount();
      setIsSearchingForAllAccounts(totalAccounts === selectedAccounts.length);
    }

    getIsAllAccountsValue();
  }, [selectedAccounts]);

  if (isError) {
    return <ErrorPage error={error} />;
  }

  return (
    <>
      <div className="mb-6 mt-8 flex flex-wrap items-center justify-between gap-4">
        <h2 className="flex items-center text-lg font-medium text-foundations-boldest">
          Overview of your payment reports
          {featureFlags.new_dashboard_summaries ? (
            <span className="ml-1 font-normal text-foundations-subtle">
              <Tooltip>
                <Tooltip.Trigger
                  render={
                    <div className="rounded-full bg-foundations-subtelst-02 p-1" />
                  }
                >
                  <InformationCircleIcon className="h-5 w-5" />
                </Tooltip.Trigger>
                <Tooltip.Popup variant="light">
                  Data is updated with a 24h delay
                </Tooltip.Popup>
              </Tooltip>
            </span>
          ) : null}
        </h2>
        {!featureFlags.new_dashboard_sr_and_ar && (
          <div className="flex flex-1 items-center justify-end space-x-4">
            <DropdownSearch
              multiple
              className="max-w-[225px]"
              label="Select account"
              selected={selectedAccounts}
              onSelect={(selected: DropdownOption<string>[]) => {
                analytics.track("insights_set_account", {
                  account_ids: [selected],
                });
                setSelectedAccounts(selected);
              }}
              getOptions={(searchTerm) =>
                getAccountsWithSearch(searchTerm).then(
                  (res) =>
                    res.data.data.map(({ columns }) => [
                      columns.id,
                      columns.name,
                    ]) || []
                )
              }
              withConfirmation
              ExtraFooterAction={
                <label className="mb-4 flex items-center space-x-2 text-sm font-medium text-foundations-bolder">
                  <Checkbox
                    checked={saveAsDefault}
                    onChange={(e) => setSaveAsDefault(e.target.checked)}
                  />
                  <span>Set as default account</span>
                </label>
              }
            />

            <DurationPicker
              startDate={startDate}
              endDate={endDate}
              setDateRange={(range) => {
                setDateRange(range);
                analytics.track("insights_set_duration", {
                  selected: {
                    start_date: range?.from
                      ? format(range?.from, "yyyy-MM-dd")
                      : "",
                    end_date: range?.to ? format(range?.to, "yyyy-MM-dd") : "",
                  },
                });
              }}
              maxDate={new Date()}
              CustomFooter={<DateRangeError range={dateRange} />}
            />
          </div>
        )}
      </div>

      {showSkeleton ? (
        <CountMetricsSkeleton />
      ) : (
        <CountMetrics
          providers={dashboardMetrics?.providers}
          flows={dashboardMetrics?.flows}
          methods={dashboardMetrics?.methods}
        />
      )}

      <div
        className={cn(
          "mt-4 grid grid-cols-1 gap-8",
          isSearchingForAllAccounts
            ? "md:grid-cols-2 lg:grid-cols-3"
            : "md:grid-cols-2"
        )}
      >
        {showSkeleton ? (
          <>
            <HorizontalBarsChartSkeleton state={skeletonState} />
            <HorizontalBarsChartSkeleton state={skeletonState} />
            {isSearchingForAllAccounts && (
              <HorizontalBarsChartSkeleton
                state={skeletonState}
                className="md:col-span-2 lg:col-span-1"
              />
            )}
          </>
        ) : (
          <>
            <ProvidersMetrics
              providers={dashboardMetrics.providers}
              currency={dashboardMetrics.currency}
            />
            <MethodsMetrics
              methods={dashboardMetrics.methods}
              currency={dashboardMetrics.currency}
            />
            {isSearchingForAllAccounts && (
              <AccountsMetrics
                revenueByAccount={dashboardMetrics.revenue_by_account}
                currency={dashboardMetrics.currency}
              />
            )}
          </>
        )}
      </div>

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-2">
        {showSkeleton ? (
          <>
            <ChartSkeleton state={skeletonState} />
            <ChartSkeleton state={skeletonState} />
          </>
        ) : (
          <>
            <RevenueMetrics
              selectedAccounts={selectedAccounts}
              revenue={dashboardMetrics.revenue_by_day}
              currency={dashboardMetrics.currency}
              totalRevenue={dashboardMetrics.total_revenue}
            />
            <TransactionsMetrics
              selectedAccounts={selectedAccounts}
              transactions={dashboardMetrics.transactions_by_day}
              totalTransactions={dashboardMetrics.total_transactions}
            />
          </>
        )}
      </div>

      {!featureFlags.new_dashboard_sr_and_ar && (
        <div className="mt-8 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          {showSkeleton ? (
            <>
              <CircleChartSkeleton state={skeletonState} />
              <CircleChartSkeleton state={skeletonState} />
              <CircleChartSkeleton state={skeletonState} />
            </>
          ) : (
            <>
              <DeclineRateMetrics
                rate={dashboardMetrics.rate_metrics.decline}
                selectedAccounts={selectedAccounts}
              />
              <SuccessRateMetrics
                rate={dashboardMetrics.rate_metrics.success}
              />
              <RefundRateMetrics rate={dashboardMetrics.rate_metrics.refund} />
            </>
          )}
        </div>
      )}
    </>
  );
};

const ProvidersMetrics = ({
  providers,
  currency,
}: {
  providers: DashboardMetrics["providers"];
  currency: DashboardMetrics["currency"];
}) => {
  const { analytics } = useAnalytics();
  const highestProvider = providers.metrics[0];

  return (
    <Card disableHover className="flex flex-col">
      <div className="mb-4">
        <Heading>Providers</Heading>
        {highestProvider && (
          <Subheading>
            {twoFixedDigit(highestProvider.percentage)}% {highestProvider.label}
          </Subheading>
        )}
      </div>
      <HorizontalBarsChart
        items={providers.metrics.map((provider) => ({
          label: provider.label,
          value: provider.amount,
          percentage: provider.percentage,
          color: getColorFromCssVariable(
            provider.label.toLocaleLowerCase() === "other"
              ? "--chart-categorical-8-subtle"
              : "--chart-categorical-1-subtler"
          ),
        }))}
        formatValue={(item) =>
          formatCurrency({
            currency,
            amount: item.value,
            compact: true,
          })
        }
        onBarHover={(showTooltip, item) => {
          if (showTooltip) {
            analytics.track("insights_provider_details", {
              provider: item.label,
              amount: formatCurrency({
                currency,
                amount: item.value,
                compact: true,
              }),
              percentage: `${twoFixedDigit(item.percentage)}%`,
            });
          }
        }}
      />
    </Card>
  );
};

const MethodsMetrics = ({
  methods,
  currency,
}: {
  methods: DashboardMetrics["methods"];
  currency: DashboardMetrics["currency"];
}) => {
  const { analytics } = useAnalytics();
  const highestMethod = methods.metrics[0];

  return (
    <Card disableHover>
      <div className="mb-4">
        <Heading>Methods</Heading>
        {highestMethod && (
          <Subheading>
            {twoFixedDigit(highestMethod.percentage)}% {highestMethod.label}
          </Subheading>
        )}
      </div>
      <HorizontalBarsChart
        items={methods.metrics.map((method) => ({
          label: method.label,
          value: method.amount,
          percentage: method.percentage,
          color: getColorFromCssVariable(
            method.label?.toLocaleLowerCase() === "other"
              ? "--chart-categorical-8-subtle"
              : "--chart-categorical-4-subtler"
          ),
        }))}
        formatValue={(item) =>
          formatCurrency({
            currency,
            amount: item.value,
            compact: true,
          })
        }
        onBarHover={(showTooltip, item) => {
          if (showTooltip) {
            analytics.track("insights_method_details", {
              method: item.label,
              amount: formatCurrency({
                currency,
                amount: item.value,
                compact: true,
              }),
              percentage: `${twoFixedDigit(item.percentage)}%`,
            });
          }
        }}
      />
    </Card>
  );
};

const AccountsMetrics = ({
  revenueByAccount,
  currency,
}: {
  revenueByAccount: DashboardMetrics["revenue_by_account"];
  currency: DashboardMetrics["currency"];
}) => {
  const highestMethod = revenueByAccount?.[0];

  return (
    <Card disableHover className="md:col-span-2 lg:col-span-1">
      <div className="mb-4">
        <Heading>Accounts</Heading>
        {highestMethod && (
          <Subheading>
            {twoFixedDigit(highestMethod.percentage)}% {highestMethod.label}
          </Subheading>
        )}
      </div>
      <HorizontalBarsChart
        items={
          revenueByAccount
            ? revenueByAccount.map((account) => ({
                label: account.label,
                value: account.amount,
                percentage: account.percentage,
                color: getColorFromCssVariable(
                  account.label.toLocaleLowerCase() === "other"
                    ? "--chart-categorical-8-subtle"
                    : "--chart-categorical-5-subtler"
                ),
              }))
            : []
        }
        formatValue={(item) =>
          formatCurrency({
            currency,
            amount: item.value,
            compact: true,
          })
        }
      />
    </Card>
  );
};

const RevenueMetrics = ({
  selectedAccounts,
  revenue,
  currency,
  totalRevenue,
}: {
  selectedAccounts: string[][];
  revenue: DashboardMetrics["revenue_by_day"];
  currency: DashboardMetrics["currency"];
  totalRevenue: DashboardMetrics["total_revenue"];
}) => {
  const [labels, data] = useMemo(
    () => [Object.keys(revenue), Object.values(revenue)],
    [revenue]
  );
  const { analytics } = useAnalytics();

  return (
    <Card disableHover className="flex flex-col">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <Heading>Total Revenue</Heading>
          <Subheading>
            {formatCurrency({ currency, amount: totalRevenue })}
          </Subheading>
        </div>
        <Button
          variant="secondary"
          size="xs"
          onClick={() => {
            analytics.track("insights_view_details", {
              source: "revenue",
            });
          }}
          render={
            <Link
              href={{
                pathname: "/transactions",
                query: {
                  account: selectedAccounts.map((account) => account[0]),
                },
              }}
            />
          }
        >
          View details
        </Button>
      </div>
      <div className="flex h-80 flex-col">
        <Chart
          key={labels.join("")}
          type="line"
          labels={labels}
          datasets={[
            {
              label: "Total Revenue",
              data,
              borderColor: getColorFromCssVariable(
                "--chart-categorical-6-subtle"
              ),
              backgroundColor: getColorFromCssVariable(
                "--chart-categorical-6-subtle"
              ),
            },
          ]}
          className="flex-1"
        >
          <Chart.Canvas className="h-full" />
          <Chart.Tooltip
            variant="line"
            valueFormatter={(value) =>
              formatCurrency({
                currency,
                amount: value,
              })
            }
          />
        </Chart>
      </div>
    </Card>
  );
};

const TransactionsMetrics = ({
  selectedAccounts,
  transactions,
  totalTransactions,
}: {
  selectedAccounts: string[][];
  transactions: DashboardMetrics["transactions_by_day"];
  totalTransactions: DashboardMetrics["total_transactions"];
}) => {
  const [labels, data] = useMemo(
    () => [Object.keys(transactions), Object.values(transactions)],
    [transactions]
  );
  const { analytics } = useAnalytics();

  const successColor = getColorFromCssVariable("--chart-categorical-2-subtler");
  const failColor = getColorFromCssVariable("--chart-categorical-3-subtle");
  const pendingColor = getColorFromCssVariable("--chart-categorical-4-subtler");
  return (
    <Card disableHover className="flex flex-col">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <Heading>Total Transactions</Heading>
          <Subheading>{formatNumber({ value: totalTransactions })}</Subheading>
        </div>
        <Button
          variant="secondary"
          size="xs"
          onClick={() => {
            analytics.track("insights_view_details", {
              source: "transactions",
            });
          }}
          render={
            <Link
              href={{
                pathname: "/transactions",
                query: {
                  account: selectedAccounts.map((account) => account[0]),
                },
              }}
            />
          }
        >
          View details
        </Button>
      </div>
      <div className="h-80">
        <Chart
          key={labels.join("")}
          type="bar"
          labels={labels}
          datasets={[
            {
              label: "SUCCESSFUL",
              data: data.map((d) => d.successful_count),
              backgroundColor: successColor,
              stack: "total",
            },
            {
              label: "FAILED",
              data: data.map((d) => d.failed_count),
              backgroundColor: failColor,
              stack: "total",
            },
            {
              label: "PENDING",
              data: data.map((d) => d.pending_count),
              backgroundColor: pendingColor,
              stack: "total",
            },
          ]}
          className="h-full flex-1"
        >
          <Chart.Legend />
          <Chart.Canvas className="h-0 flex-1" />
          <Chart.Tooltip valueFormatter={(value) => formatNumber({ value })} />
        </Chart>
      </div>
    </Card>
  );
};

const DeclineRateMetrics = ({
  selectedAccounts,
  rate,
}: {
  selectedAccounts: string[][];
  rate: DashboardMetrics["rate_metrics"]["decline"];
}) => {
  const { total, providers, errors } = rate;
  const { analytics } = useAnalytics();

  return (
    <Card disableHover className="flex flex-col">
      <div className="mb-4 flex h-[30px] items-center justify-between">
        <Heading className="text-foundations-boldest">Declines rate</Heading>
        <Button
          variant="secondary"
          size="xs"
          onClick={() => {
            analytics.track("insights_view_details", {
              source: "declines",
            });
          }}
          render={
            <Link
              href={{
                pathname: "/transactions",
                query: {
                  account: selectedAccounts.map((account) => account[0]),
                },
              }}
            />
          }
        >
          View details
        </Button>
      </div>
      <Tabs
        onTabClick={(tab) => {
          analytics.track("insights_tab_view", {
            source: "declines_rate",
            tab: tab.label,
          });
        }}
        tabs={[
          {
            label: "Total",
            Panel: () => (
              <div className="flex justify-center">
                <CircleChart value={total.percentage} variant="danger">
                  {!!total.diffPercentage && (
                    <DiffPercentageBadge
                      diffPercentage={total.diffPercentage}
                      variant={total?.diffPercentage > 0 ? "danger" : "success"}
                    />
                  )}
                </CircleChart>
              </div>
            ),
          },
          {
            disabled: !total.percentage,
            label: "Providers",
            Panel: () => {
              return (
                <div>
                  <p className="text-xs uppercase text-foundations-subtle">
                    Top {providers.length} Providers
                  </p>
                  <div className="mt-4 space-y-4">
                    {providers.map((provider) => (
                      <RateItem
                        key={provider.name}
                        label={provider.name}
                        img={provider.image}
                        percentage={provider.percentage}
                        diffPercentage={provider.diffPercentage}
                        variant={
                          !provider.diffPercentage ||
                          provider.diffPercentage > 0
                            ? "danger"
                            : "success"
                        }
                      />
                    ))}
                  </div>
                </div>
              );
            },
          },
          {
            disabled: !total.percentage,
            label: "Errors",
            Panel: () => (
              <div>
                <p className="text-xs uppercase text-foundations-subtle">
                  Top {errors.length} Error Codes
                </p>
                <div className="mt-4 space-y-4">
                  {errors.map((error, idx) => (
                    <RateItem
                      key={idx}
                      rateType="error"
                      label={error.code}
                      percentage={error.percentage}
                      diffPercentage={error.diffPercentage}
                      count={error.count}
                      variant={
                        !error.diffPercentage || error.diffPercentage > 0
                          ? "danger"
                          : "success"
                      }
                    />
                  ))}
                </div>
              </div>
            ),
          },
        ]}
      />
    </Card>
  );
};

const SuccessRateMetrics = ({
  rate,
}: {
  rate: DashboardMetrics["rate_metrics"]["success"];
}) => {
  const { total, providers, methods } = rate;
  const { analytics } = useAnalytics();
  return (
    <Card disableHover>
      <div className="mb-4 flex h-[30px] items-center">
        <Heading className="text-foundations-boldest">Success rate</Heading>
      </div>
      <Tabs
        onTabClick={(tab) => {
          analytics.track("insights_tab_view", {
            source: "success_rate",
            tab: tab.label,
          });
        }}
        tabs={[
          {
            label: "Total",
            Panel: () => (
              <div className="flex justify-center">
                <CircleChart value={total.percentage} variant="success">
                  {!!total.diffPercentage && (
                    <DiffPercentageBadge
                      diffPercentage={total.diffPercentage}
                      variant={total?.diffPercentage > 0 ? "success" : "danger"}
                    />
                  )}
                </CircleChart>
              </div>
            ),
          },
          {
            disabled: !total.percentage,
            label: "Providers",
            Panel: () => {
              return (
                <div>
                  <p className="text-xs uppercase text-foundations-subtle">
                    Top {providers.length} Providers
                  </p>
                  <div className="mt-4 space-y-4">
                    {providers.map((provider) => (
                      <RateItem
                        key={provider.name}
                        label={provider.name}
                        img={provider.image}
                        percentage={provider.percentage}
                        diffPercentage={provider.diffPercentage}
                        variant={
                          !provider.diffPercentage ||
                          provider.diffPercentage > 0
                            ? "success"
                            : "danger"
                        }
                      />
                    ))}
                  </div>
                </div>
              );
            },
          },
          {
            disabled: !total.percentage,
            label: "Methods",
            Panel: () => {
              return (
                <div>
                  <p className="text-xs uppercase text-foundations-subtle">
                    Top {methods.length} payment methods
                  </p>
                  <div className="mt-4 space-y-4">
                    {methods.map((method) => (
                      <RateItem
                        key={method.name}
                        label={method.name}
                        img={method.image}
                        percentage={method.percentage}
                        diffPercentage={method.diffPercentage}
                        variant={
                          !method.diffPercentage || method.diffPercentage > 0
                            ? "success"
                            : "danger"
                        }
                      />
                    ))}
                  </div>
                </div>
              );
            },
          },
        ]}
      />
    </Card>
  );
};

const RefundRateMetrics = ({
  rate,
}: {
  rate: DashboardMetrics["rate_metrics"]["refund"];
}) => {
  const { total } = rate;
  return (
    <Card disableHover>
      <div className="mb-4 flex h-[30px] items-center">
        <Heading className="text-foundations-boldest">Refund rate</Heading>
      </div>
      <Tabs
        tabs={[
          {
            label: "Total",
            Panel: () => (
              <div className="flex justify-center">
                <CircleChart value={total.percentage}>
                  {!!total.diffPercentage && (
                    <DiffPercentageBadge
                      diffPercentage={total.diffPercentage}
                    />
                  )}
                </CircleChart>
              </div>
            ),
          },
        ]}
      />
    </Card>
  );
};

const getAccountsCount = (): Promise<number> =>
  axiosApiInstance
    .get<{ count: number }>(`${process.env.NEXT_PUBLIC_API_BASE}/d/accounts/`, {
      params: {
        search: "",
        offset: 0,
        limit: 10,
      },
    })
    .then((res) => {
      return res.data.count;
    })
    .catch((err) => {
      return Promise.reject(parseErrorsToArray(err)?.[0]);
    });

const getDashboardData = ({
  startDate,
  endDate,
  accountIds,
  signal,
}: {
  startDate: Date;
  endDate: Date;
  accountIds?: string;
  signal?: AbortSignal;
}) => {
  const start = new Date(
    new Date(startDate).getTime() -
      new Date(startDate).getTimezoneOffset() * 60 * 1000
  )
    .toISOString()
    .split("T")[0];
  const end = new Date(
    new Date(endDate).getTime() -
      new Date(endDate).getTimezoneOffset() * 60 * 1000
  )
    .toISOString()
    .split("T")[0];

  return axiosApiInstance
    .get("/d/metrics/dashboard/", {
      signal,
      params: {
        start_date: start,
        end_date: end,
        account_ids: accountIds || undefined,
      },
    })
    .then((res) => res.data);
};

export const DateRangeError = ({ range }: { range?: DateRange }) => {
  if (range && range.from && range.to) return null;

  return (
    <div className="p-4 text-center text-sm text-danger">
      {!range
        ? "Please select a date range."
        : !range.to
          ? "Please select an end date."
          : null}
    </div>
  );
};
